"use client";

import type { ReactNode } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { MindstringPrototype, type ThoughtGrouping } from "@/generated/fastapi";
import { useUserChoices } from "@/state/globalStore";
import { useSystemDesign } from "@/shared/system-design";
import type { StudioLayoutMap } from "../types";
import StudioArrangementDndProvider from "../workflows/arrangement/StudioArrangementDndProvider";
import StudioPrototypeTabs from "./StudioPrototypeTabs";

interface StudioLayoutProps {
  studioLayoutMap: StudioLayoutMap;
  activeStudioGrouping: ThoughtGrouping;
  activeStudioExportLock: boolean;
  viewHeaderComponent?: ReactNode;
  situationContentComponent: (props: {
    situationId: string;
    prototype: MindstringPrototype;
  }) => ReactNode;
  noSelectionFallback?: (props: {
    prototypeCategory: MindstringPrototype;
  }) => ReactNode;
}

export default function StudioLayout({
  studioLayoutMap,
  activeStudioGrouping,
  activeStudioExportLock,
  viewHeaderComponent,
  situationContentComponent,
  noSelectionFallback,
}: StudioLayoutProps) {
  const { isMobileDevice } = useSystemDesign();
  const {
    activePrototypeCategory,
    setActivePrototypeCategory,
    interimState,
  } = useUserChoices();

  const isLocked = activeStudioExportLock;
  const isFreeList = activeStudioGrouping === "free_list";
  const isManualAddGuidanceActive =
    interimState.profile_shaping_guidance?.isOpen === true &&
    interimState.profile_shaping_guidance?.value === "manual_add";

  const prototypeTabs = (
    <StudioPrototypeTabs
      activePrototypeCategory={activePrototypeCategory}
      isFreeList={isFreeList}
      isLocked={isLocked}
      isManualAddGuidanceActive={isManualAddGuidanceActive}
      noSelectionFallback={noSelectionFallback}
      onPrototypeCategoryChange={setActivePrototypeCategory}
      situationContentComponent={situationContentComponent}
      studioLayoutMap={studioLayoutMap}
    />
  );

  return (
    <Box>
      <VStack align="stretch" spaceY={4}>
        {viewHeaderComponent}

        {isLocked && !isFreeList ? (
          <StudioArrangementDndProvider isMobileDevice={isMobileDevice}>
            {prototypeTabs}
          </StudioArrangementDndProvider>
        ) : (
          prototypeTabs
        )}
      </VStack>
    </Box>
  );
}
